import { DialogRef } from '@angular/cdk/dialog';

/**
 * Reference to a dialog opened via {@link DialogService.open}. Returned to the opener and
 * also injectable by the opened content component/template (to close with a result).
 *
 * Wraps the CDK `DialogRef`. `closed` is a `Promise` that resolves with the close result — or
 * `undefined` when the dialog is dismissed (close button / Escape / backdrop). CDK emits its
 * own `closed` synchronously inside `close()`, which can trigger `ExpressionChangedAfter-
 * ItHasBeenChecked` (NG0100) when a subscriber updates view state; a Promise defers to a
 * microtask, so handlers are change-detection safe. Using a Promise (rather than an rxjs
 * Observable) also keeps the library rxjs-free and mirrors ng-bootstrap's `NgbModalRef.result`.
 */
export class CompsychDialogRef<R = unknown> {
  /** Resolves once the dialog has closed, with the result (or `undefined` when dismissed). */
  readonly closed: Promise<R | undefined>;

  constructor(private readonly _cdkRef: DialogRef<R, unknown>) {
    this.closed = new Promise<R | undefined>((resolve) => {
      this._cdkRef.closed.subscribe((result) => resolve(result));
    });
  }

  /** The opened component instance, or `null` for template content. */
  get componentInstance(): unknown {
    return this._cdkRef.componentInstance;
  }

  /** Unique id of the open dialog. */
  get id(): string {
    return this._cdkRef.id;
  }

  /** Closes the dialog, optionally passing a result to `closed`. */
  close(result?: R): void {
    this._cdkRef.close(result);
  }
}
